import React, { useRef, useState, useEffect } from "react";
import { UploadCloud, FileText, X } from "lucide-react";
import PropTypes from "prop-types";
import { MAX_TOTAL_UPLOAD_SIZE, ALLOWED_EXTENSIONS } from "../../constants";

const ACCEPT = ALLOWED_EXTENSIONS.join(",");
const MAX_TOTAL_MB = MAX_TOTAL_UPLOAD_SIZE / (1024 * 1024);

export default function FileUpload({ files, onChange}) {
  const inputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState(files || []);
  const [dragging, setDragging]           = useState(false);
  const [typeError, setTypeError]         = useState(null);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getExtension = (file) =>
    file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

  const validateFiles = (incoming) => {
    const validFiles   = [];
    const invalidFiles = [];

    incoming.forEach((file) => {
      if (ALLOWED_EXTENSIONS.includes(getExtension(file))) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length) {
      setTypeError(
        <>{invalidFiles.map((f) => f.name).join(", ")} — only {ALLOWED_EXTENSIONS.join(", ")} accepted.</>
      );
    }

    return validFiles;
  };

  const totalBytes = (fileList) =>
    fileList.reduce((sum, f) => sum + f.size, 0);

  useEffect(() => {
    if (files && files.length > 0) {
      setUploadedFiles(files);
    }
  }, [files]);

  const addFiles = (incoming) => {
    setTypeError(null);
    const validFiles = validateFiles(Array.from(incoming));
    if (!validFiles.length) return;

    if (totalBytes([...uploadedFiles, ...validFiles]) > MAX_TOTAL_UPLOAD_SIZE) {
      setTypeError(<>Total upload size exceeds the <b>{MAX_TOTAL_MB} MB</b> limit.</>);
      return;
    }

    setUploadedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    onChange?.([...uploadedFiles, ...validFiles]);
  };

  const removeFile = (name) => {
    setTypeError(null);
    const updated = uploadedFiles.filter((f) => f.name !== name);
    setUploadedFiles(updated);
    onChange?.(updated);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleInput = (e) => {
    addFiles(e.target.files);
    e.target.value = null;
  };

  return (
    <div className="flex flex-col gap-1.5">
      

      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        className={[
          "flex items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-4 transition-colors duration-150 cursor-pointer",
          dragging
            ? "border-blue-400 bg-blue-50"
            : "border-gray-200 bg-gray-50 hover:border-blue-500 hover:bg-blue-50",
        ].join(" ")}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={handleInput}
        />
        <UploadCloud size={22} className="shrink-0 text-slate-400" />
        <div>
          <p className="text-sm font-medium text-slate-700">
            Drag &amp; drop or{" "}
            <span className="text-blue-600 underline underline-offset-2">browse</span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Supported formats: {ALLOWED_EXTENSIONS.join(", ")}
          </p>
        </div>
      </div>

      {/* File list */}
      {uploadedFiles.length > 0 && (
        <ul className="flex flex-col gap-1.5 mt-1">
          {uploadedFiles.map((f) => (
            <li
              key={f.name}
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-violet-50 border border-violet-100"
            >
              <FileText size={16} className="shrink-0 text-blue-600" />
              <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{f.name}</span>
              <span className="text-xs text-slate-400 shrink-0">{formatFileSize(f.size)}</span>
              <button
                type="button"
                onClick={() => removeFile(f.name)}
                className="shrink-0 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-gray-200 transition-colors"
                aria-label={`Remove ${f.name}`}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {typeError && <p className="text-xs text-red-500 mt-0.5">{typeError}</p>}
    </div>
  );
}

FileUpload.propTypes = {
  files:    PropTypes.array,
  onChange: PropTypes.func
};
